// Frontend mirror of the comprehensive source configuration
// This ensures UI components use the same source definitions as the backend

export interface SourceCategory {
  id: string;
  label: string;
  description: string;
  color?: string;
}

// Mirror the backend source categories for frontend use
export const SOURCE_CATEGORIES: Record<string, SourceCategory> = {
  'academic': {
    id: 'academic',
    label: 'Academic & Research',
    description: 'Peer-reviewed journals, universities, research institutions',
    color: 'blue'
  },
  'business': {
    id: 'business',
    label: 'Business & Financial',
    description: 'Business news, financial data, market analysis',
    color: 'green'
  },
  'news': {
    id: 'news',
    label: 'News & Media',
    description: 'Major news outlets, journalism, current events',
    color: 'red'
  },
  'technology': {
    id: 'technology',
    label: 'Technology & Innovation',
    description: 'Tech news, innovation, product launches, industry analysis',
    color: 'purple'
  },
  'government': {
    id: 'government',
    label: 'Government & Policy',
    description: 'Official government sources, policy documents, regulations',
    color: 'indigo'
  },
  'social_forums': {
    id: 'social_forums',
    label: 'Social Media & Forums',
    description: 'Social platforms, discussion forums, community content',
    color: 'amber'
  },
  'industry_trade': {
    id: 'industry_trade',
    label: 'Industry & Trade',
    description: 'Industry publications, trade associations, sector-specific content',
    color: 'orange'
  },
  'think_tanks': {
    id: 'think_tanks',
    label: 'Think Tanks & Policy',
    description: 'Policy research, think tanks, analytical institutions',
    color: 'cyan'
  },
  'educational': {
    id: 'educational',
    label: 'Educational Resources',
    description: 'Universities, educational content, online courses',
    color: 'teal'
  },
  'healthcare_medical': {
    id: 'healthcare_medical',
    label: 'Healthcare & Medical',
    description: 'Medical journals, health organizations, clinical data',
    color: 'rose'
  },
  'legal_regulatory': {
    id: 'legal_regulatory',
    label: 'Legal & Regulatory',
    description: 'Legal databases, court documents, regulatory information',
    color: 'slate'
  },
  'environmental': {
    id: 'environmental',
    label: 'Environmental & Sustainability',
    description: 'Environmental data, climate research, sustainability reports',
    color: 'emerald'
  },
  'international': {
    id: 'international',
    label: 'International Organizations',
    description: 'UN, World Bank, IMF, and other international bodies',
    color: 'violet'
  },
  'startup_innovation': {
    id: 'startup_innovation',
    label: 'Startups & Innovation',
    description: 'Startup news, venture capital, innovation ecosystems',
    color: 'pink'
  },
  'general': {
    id: 'general',
    label: 'General Web',
    description: 'Broader web content, mixed sources',
    color: 'gray'
  }
};

// Combination categories for mixed searches
export const COMBINATION_CATEGORIES = {
  'academic_business': {
    id: 'academic_business',
    label: 'Academic + Business',
    description: 'Combination of academic research and business sources'
  },
  'business_news': {
    id: 'business_news',
    label: 'Business + News',
    description: 'Business and general news sources'
  },
  'technology_startup': {
    id: 'technology_startup',
    label: 'Technology + Startups',
    description: 'Tech news and startup ecosystem'
  },
  'government_policy': {
    id: 'government_policy',
    label: 'Government + Policy',
    description: 'Government sources and policy think tanks'
  }
};

// Get all available source options for UI components
export function getSourceTypeOptions() {
  const singleCategories = Object.values(SOURCE_CATEGORIES).map(category => ({
    value: category.id,
    label: category.label
  }));

  const combinationOptions = Object.values(COMBINATION_CATEGORIES).map(combo => ({
    value: combo.id,
    label: combo.label
  }));

  return [
    { value: 'none', label: 'None' },
    { value: 'general', label: 'General Web' },
    ...singleCategories.filter(cat => cat.value !== 'general'),
    ...combinationOptions
  ];
}

// Validate source selection
export function isValidSourceId(sourceId: string): boolean {
  return !!(SOURCE_CATEGORIES[sourceId] || COMBINATION_CATEGORIES[sourceId]);
}

// Get source display information
export function getSourceInfo(sourceId: string): { label: string; description: string } | null {
  const category = SOURCE_CATEGORIES[sourceId];
  const combination = COMBINATION_CATEGORIES[sourceId];
  
  if (category) {
    return { label: category.label, description: category.description };
  }
  
  if (combination) {
    return { label: combination.label, description: combination.description };
  }
  
  return null;
} 